import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators'
import { AuthService } from './@core/services/auth.service';
import { KeyboardEventsService } from './@core/services/keyboard-events.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    host: {
        '(document:keydown)': 'handleKeyboardEvents($event)'
    },
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  inProgress: InteractionStatus = InteractionStatus.Startup;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private authService: AuthService,
    private keyboardEventsService: KeyboardEventsService
  ){
    this.checkAndSetActiveAccount();

    if (window.innerWidth < 900) {
      this.isMobileResolution = true;
    } else {
      this.isMobileResolution = false;
    }

    this.applyEnvironmentThemeClass();
  }

  isMobileResolution: boolean;

  @HostListener("window:resize", ["$event"])
  isMobile(event) {
    if (window.innerWidth < 900) {
      this.isMobileResolution = true;
    } else {
      this.isMobileResolution = false;
    }
  }

  ngOnInit(): void {

    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

    this.msalBroadcastService.inProgress$
      .pipe(
        takeUntil(this._destroying$)
      )
      .subscribe((status: InteractionStatus) => {
        this.inProgress = status;
        if (status !== InteractionStatus.None)
          return;

        this.setLoginDisplay();
        this.checkAndSetActiveAccount();

        if (this.router.url === '/login' && this.authService.hasActiveAccount()) {
          this.router.navigate(['/welcome']);
          return;
        }

        if (this.msalService.instance.getAllAccounts().length > 0) {
          this.authService.getAuthorization();
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((message: EventMessage) => message.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((message: EventMessage) => {
        const payload = message.payload as AuthenticationResult;
        if (payload?.account) {
          this.msalService.instance.setActiveAccount(payload.account);
        }

        this.router.navigate(['/welcome']);
      });
  }

  handleKeyboardEvents(event: KeyboardEvent) {
    this.keyboardEventsService.updated.next(event);
  }

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount(){
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  shouldRenderRouterOutlet(): boolean {
    return !this.isLoginTransitionPending();
  }

  shouldShowAuthTransitionLoading(): boolean {
    return this.isLoginTransitionPending();
  }

  private isLoginTransitionPending(): boolean {
    if (this.router.url !== '/login')
      return false;

    return this.inProgress !== InteractionStatus.None || this.authService.hasActiveAccount();
  }

  shouldShowNavbar(): boolean {
    if (this.isHomepageUrl(this.router.url) || this.router.url === '/login')
      return false;

    return this.authService.hasActiveAccount();
  }

  private isHomepageUrl(url: string): boolean {
    return url.split(/[?#]/, 1)[0] === '/';
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
  private applyEnvironmentThemeClass(): void {
    const body = document.body;

    body.classList.remove('env-production', 'env-non-production');

    if (environment.production) {
      body.classList.add('env-production');
      return;
    }

    body.classList.add('env-non-production');
  }
}
